/**
 * Free Shipping Progress Bar
 */

if (!customElements.get('free-shipping-progress-bar')) {
  let currentPercent = 0;

  class FreeShippingProgressBar extends HTMLElement {
    constructor() {
      super();
      this.animationFrame = null;
    }

    connectedCallback() {
      this.updateStatus();
    }

    disconnectedCallback() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    }

    updateStatus() {
      // Find elements safely
      const remainingAmountText = this.querySelector(
        '.free-shipping__info-text strong'
      );

      const remainingInfo = this.querySelector(
        '.free-shipping__info-text'
      );

      const eligibilityInfo = this.querySelector(
        '.free-shipping__info-eligibility'
      );

      // Cart total is stored in cents
      const cartTotal =
        parseInt(this.dataset.cartTotal, 10) || 0;

      // Free shipping threshold
      const shippingAmount =
        parseInt(this.dataset.shippingAmount, 10) || 0;

      const currencyRate =
        typeof Shopify !== 'undefined' &&
        Shopify.currency &&
        Shopify.currency.rate
          ? parseFloat(Shopify.currency.rate)
          : 1;

      const freeShippingThreshold = Math.round(
        shippingAmount * currencyRate
      );

      // Prevent division by zero
      if (freeShippingThreshold <= 0) {
        this.style.setProperty('--percent', '100%');

        if (remainingInfo) {
          remainingInfo.style.display = 'none';
        }

        if (eligibilityInfo) {
          eligibilityInfo.style.display = 'block';
        }

        return;
      }

      const isEligible =
        cartTotal >= freeShippingThreshold;

      // Update remaining amount
      if (remainingAmountText && !isEligible) {
        const remainingAmount =
          freeShippingThreshold - cartTotal;

        const currencyFormat =
          window.theme &&
          window.theme.settings &&
          window.theme.settings.money_with_currency_format
            ? window.theme.settings.money_with_currency_format
            : '${{amount}}';

        if (typeof formatMoney === 'function') {
          remainingAmountText.innerHTML =
            formatMoney(
              remainingAmount,
              currencyFormat
            );
        }
      }

      // Safely update info section
      if (remainingInfo) {
        remainingInfo.style.display =
          isEligible ? 'none' : 'block';
      }

      // Safely update eligibility section
      if (eligibilityInfo) {
        eligibilityInfo.style.display =
          isEligible ? 'block' : 'none';
      }

      // Calculate progress
      const progressPercentage = isEligible
        ? 1
        : Math.min(
            cartTotal / freeShippingThreshold,
            1
          );

      this.animateProgressBar(
        progressPercentage
      );
    }

    animateProgressBar(progressPercentage) {
      const targetPercent =
        Math.max(
          0,
          Math.min(progressPercentage * 100, 100)
        );

      const startPercent = currentPercent;
      const duration = 200;
      const startTime = performance.now();

      const ease = (t) => t * (2 - t);

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      const animate = (time) => {
        const elapsed = time - startTime;

        const progress = Math.min(
          elapsed / duration,
          1
        );

        const easedProgress = ease(progress);

        currentPercent =
          startPercent +
          (targetPercent - startPercent) *
            easedProgress;

        this.style.setProperty(
          '--percent',
          `${currentPercent}%`
        );

        if (progress < 1) {
          this.animationFrame =
            requestAnimationFrame(animate);
        } else {
          currentPercent = targetPercent;

          this.style.setProperty(
            '--percent',
            `${currentPercent}%`
          );

          this.animationFrame = null;
        }
      };

      this.animationFrame =
        requestAnimationFrame(animate);
    }
  }

  customElements.define(
    'free-shipping-progress-bar',
    FreeShippingProgressBar
  );
}